import type {
  GuidedSection,
  SectionStatusMap,
  OrderInfoState,
  CaseInfoState,
} from '@lds-efsp/shared-contracts';

/**
 * The guided-section guard applied to an AI extraction patch
 * (SPEC-SOR-174 R16/R19/R20/R27).
 *
 * `applyExtraction` computes what a patch WOULD write, unchanged — the R88
 * filing-type guards, `routeExtractedCaseCategory`, `mergeExtractedParties`
 * and `mergeExtractedAttorneys` all run exactly as before. This module is the
 * single chokepoint that then decides what may actually land:
 *
 *  - R20 — a section already `confirmed` has NO field written and stays
 *    `confirmed`. Enforced by dropping the section's keys from the computed
 *    updates rather than by redacting the incoming patch: redaction would
 *    silently flip the R88 block's `!patch.caseNumber` branch into inferring
 *    "New case" over a confirmed case section, which is the opposite of
 *    protection.
 *  - R19 — a section the patch touched while `pending` becomes `prefilled`.
 *  - R27 — no patch ever raises any section to `confirmed`. Only the filer's
 *    own confirm act does that, so a `prefilled` section is no closer to
 *    confirmed than a `pending` one.
 *  - R16 — with the confirm flow off the whole thing is inert: updates pass
 *    through untouched and no status map is ever written.
 *
 * Pure and total, so the classic flow and the confirm flow share one
 * implementation and the spec property suite can exercise it directly.
 */

/**
 * Which section owns an `OrderInfoState` key `applyExtraction` may write.
 *
 * TOTAL by construction rather than an enumerated allow-list: the filing court
 * is `orderInfo.state` + `orderInfo.county`, and the only other key extraction
 * writes is `filingType`, which belongs to the case section (it is what R6/R7
 * read to decide the case section's readiness). Defaulting the remainder to
 * the court section means a key added here later is protected by default
 * instead of silently escaping the guard.
 */
export function sectionOfOrderInfoKey(key: string): GuidedSection {
  return key === 'filingType' ? 'sec_case' : 'sec_court';
}

/**
 * Which section owns a `CaseInfoState` key `applyExtraction` may write.
 *
 * Total for the same reason. `courtJurisdiction` is the one court-owned key
 * here — a county-scoped sub-location, displayed in the Court section beneath
 * County (R64) — and it is only ever written as the stale-clear that rides
 * along with a county write, so it can never be the sole reason the court
 * section counts as touched.
 */
export function sectionOfCaseInfoKey(key: string): GuidedSection {
  return key === 'courtJurisdiction' ? 'sec_court' : 'sec_case';
}

/**
 * Which section owns an `ocrPopulatedFields` entry — the "detected" chips.
 * Total, and deliberately the same shape as the two above.
 */
export function sectionOfPopulatedField(field: string): GuidedSection {
  if (field === 'county') return 'sec_court';
  if (field === 'parties' || field === 'attorneys') return 'sec_parties';
  return 'sec_case';
}

export interface ExtractionSectionGuardInput {
  /** The site-level gate. False leaves every output identical to its input. */
  confirmFlowEnabled: boolean;
  /** The draft's section statuses BEFORE the patch is applied. */
  statuses: SectionStatusMap | undefined;
  /** What the patch would write to `orderInfo`. */
  orderUpdates: Partial<OrderInfoState>;
  /** What the patch would write to `caseInfo`. */
  caseUpdates: Partial<CaseInfoState>;
  /** Whether the patch carried a party/attorney roster at all. */
  rosterTouched: boolean;
  /** The `ocrPopulatedFields` entries the patch would claim. */
  populated: Set<string>;
}

export interface ExtractionSectionGuardResult {
  orderUpdates: Partial<OrderInfoState>;
  caseUpdates: Partial<CaseInfoState>;
  /**
   * False when `sec_parties` is confirmed — the caller must keep the PRIOR
   * rosters rather than the merged ones. The merge helpers still ran; their
   * result is simply not adopted, so R20 holds without changing them.
   */
  applyRoster: boolean;
  populated: Set<string>;
  /** The statuses to persist. `undefined` means "write no map" (R16). */
  sectionStatuses: SectionStatusMap | undefined;
}

/** The sections a patch touches, read off what it would actually write. */
export function sectionsTouchedByUpdates(input: {
  orderUpdates: Partial<OrderInfoState>;
  caseUpdates: Partial<CaseInfoState>;
  rosterTouched: boolean;
}): Set<GuidedSection> {
  const touched = new Set<GuidedSection>();
  for (const key of Object.keys(input.orderUpdates)) touched.add(sectionOfOrderInfoKey(key));
  for (const key of Object.keys(input.caseUpdates)) touched.add(sectionOfCaseInfoKey(key));
  if (input.rosterTouched) touched.add('sec_parties');
  return touched;
}

/**
 * R19 + R27: every touched section that is not already `confirmed` becomes
 * `prefilled`; a `confirmed` section is left alone. Nothing here can produce
 * `confirmed`, which is R27 by construction — the value is unreachable from
 * this function's body.
 */
export function advanceSectionStatuses(
  prev: SectionStatusMap | undefined,
  touched: Iterable<GuidedSection>,
): SectionStatusMap | undefined {
  const next: SectionStatusMap = { ...prev };
  let changed = false;
  for (const section of touched) {
    if (next[section] === 'confirmed' || next[section] === 'prefilled') continue;
    next[section] = 'prefilled';
    changed = true;
  }
  if (!changed) return prev;
  return next;
}

/** Drop every entry of `updates` whose owning section is already confirmed. */
function withoutConfirmedKeys<T extends object>(
  updates: T,
  statuses: SectionStatusMap | undefined,
  sectionOf: (key: string) => GuidedSection,
): T {
  const kept = Object.entries(updates).filter(
    ([key]) => statuses?.[sectionOf(key)] !== 'confirmed',
  );
  return Object.fromEntries(kept) as T;
}

export function guardExtractionSections(
  input: ExtractionSectionGuardInput,
): ExtractionSectionGuardResult {
  const { confirmFlowEnabled, statuses, orderUpdates, caseUpdates, rosterTouched, populated } =
    input;

  // R16: the guided-section model is inert in the classic flow — no status is
  // written and nothing the patch computed is withheld.
  if (!confirmFlowEnabled) {
    return {
      orderUpdates,
      caseUpdates,
      applyRoster: true,
      populated,
      sectionStatuses: statuses,
    };
  }

  // Read the touch off the UNFILTERED updates: what the patch carried is a
  // fact about the patch, not about which sections happened to accept it.
  const touched = sectionsTouchedByUpdates({ orderUpdates, caseUpdates, rosterTouched });

  return {
    orderUpdates: withoutConfirmedKeys(orderUpdates, statuses, sectionOfOrderInfoKey),
    caseUpdates: withoutConfirmedKeys(caseUpdates, statuses, sectionOfCaseInfoKey),
    applyRoster: statuses?.sec_parties !== 'confirmed',
    populated: new Set(
      Array.from(populated).filter(
        (field) => statuses?.[sectionOfPopulatedField(field)] !== 'confirmed',
      ),
    ),
    sectionStatuses: advanceSectionStatuses(statuses, touched),
  };
}
